import React, { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import TagManager from 'react-gtm-module';
import LoadingSpinner from '../common/LoadingSpinner';
import { getCookie } from '../utils/utils';
import './FeedbackModal.scss';

type FeedbackModalProps = {
  isOpen: boolean;
  onClose: () => void;
  isLoggedIn: boolean;
  userEmail?: string;
};

type FeedbackType = 'bug' | 'feature' | 'general' | 'improvement';

const FeedbackModal = ({ isOpen, onClose, isLoggedIn, userEmail }: FeedbackModalProps) => {
  const { t } = useTranslation();
  const [feedbackType, setFeedbackType] = useState<FeedbackType>('general');
  const [email, setEmail] = useState(userEmail || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleClose = () => {
    if (!isSubmitting) {
      // Reset form
      setFeedbackType('general');
      setEmail(userEmail || '');
      setSubject('');
      setMessage('');
      setSubmitStatus(null);
      setErrorMessage('');
      onClose();
    }
  };

  const validateForm = () => {
    if (!message.trim()) {
      setErrorMessage(t('feedback.validation.messageRequired'));
      return false;
    }
    
    if (!isLoggedIn && !email.trim()) {
      setErrorMessage(t('feedback.validation.emailRequired'));
      return false;
    }
    
    if (!isLoggedIn && email && !isValidEmail(email)) {
      setErrorMessage(t('feedback.validation.emailInvalid'));
      return false;
    }
    
    return true;
  };

  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage('');

    try {
      TagManager.dataLayer({
        dataLayer: {
          event: 'feedback_submitted',
          category: 'User Interaction',
          action: 'Submit',
          label: `Feedback Type: ${feedbackType}`,
        },
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isLoggedIn && { 'X-CSRF-TOKEN': getCookie('csrf_access_token') }),
        },
        credentials: isLoggedIn ? 'include' : 'omit',
        body: JSON.stringify({
          type: feedbackType,
          email: isLoggedIn ? undefined : email,
          subject: subject.trim() || undefined,
          message: message.trim(),
          userAgent: navigator.userAgent,
          url: window.location.href,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('feedback.error.submitFailed'));
      }

      setSubmitStatus('success');
      
      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
      setErrorMessage(error instanceof Error ? error.message : t('feedback.error.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFeedbackTypeLabel = (type: FeedbackType) => {
    switch (type) {
      case 'bug':
        return t('feedback.type.bug');
      case 'feature':
        return t('feedback.type.feature');
      case 'improvement':
        return t('feedback.type.improvement');
      case 'general':
      default:
        return t('feedback.type.general');
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      className="feedback-modal"
      backdrop={isSubmitting ? 'static' : true}
      keyboard={!isSubmitting}
    >
      <Modal.Header closeButton={!isSubmitting}>
        <Modal.Title>
          <Trans i18nKey="feedback.title">Send Feedback</Trans>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {submitStatus === 'success' && (
          <Alert variant="success" className="feedback-modal__success-alert">
            <div className="d-flex align-items-center">
              <span className="me-2">✓</span>
              <Trans i18nKey="feedback.success">
                Thank you for your feedback! We'll get back to you soon.
              </Trans>
            </div>
          </Alert>
        )}

        {submitStatus === 'error' && (
          <Alert variant="danger" className="feedback-modal__error-alert">
            {errorMessage}
          </Alert>
        )}

        {submitStatus !== 'success' && (
          <Form onSubmit={handleSubmit}>
            <div className="feedback-modal__intro">
              <Trans i18nKey="feedback.intro">
                We'd love to hear from you! Your feedback helps us improve Jaakko.com.
              </Trans>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>
                <Trans i18nKey="feedback.type.label">Feedback Type</Trans>
              </Form.Label>
              <Form.Control
                as="select"
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value as FeedbackType)}
                disabled={isSubmitting}
              >
                <option value="general">{getFeedbackTypeLabel('general')}</option>
                <option value="bug">{getFeedbackTypeLabel('bug')}</option>
                <option value="feature">{getFeedbackTypeLabel('feature')}</option>
                <option value="improvement">{getFeedbackTypeLabel('improvement')}</option>
              </Form.Control>
            </Form.Group>

            {!isLoggedIn && (
              <Form.Group className="mb-3">
                <Form.Label>
                  <Trans i18nKey="feedback.email.label">Your Email</Trans>
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('feedback.email.placeholder')}
                  disabled={isSubmitting}
                  required
                />
                <Form.Text className="text-muted">
                  <Trans i18nKey="feedback.email.help">
                    We'll use this to respond to your feedback.
                  </Trans>
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Label>
                <Trans i18nKey="feedback.subject.label">Subject</Trans>
                <span className="text-muted"> ({t('common.optional')})</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder={t('feedback.subject.placeholder')}
                disabled={isSubmitting}
                maxLength={100}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <Trans i18nKey="feedback.message.label">Your Feedback</Trans>
                <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('feedback.message.placeholder')}
                disabled={isSubmitting}
                required
                maxLength={2000}
                className="feedback-modal__message-input"
              />
              <Form.Text className="text-muted">
                {message.length}/2000
              </Form.Text>
            </Form.Group>

            <div className="feedback-modal__footer-buttons">
              <Button
                variant="outline-secondary"
                onClick={handleClose}
                disabled={isSubmitting}
                className="me-2"
              >
                <Trans i18nKey="common.cancel">Cancel</Trans>
              </Button>
              <Button
                variant="primary"
                type="submit"
                disabled={isSubmitting}
                className="feedback-modal__submit-button"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner className="me-2" />
                    <Trans i18nKey="feedback.submitting">Sending...</Trans>
                  </>
                ) : (
                  <Trans i18nKey="feedback.submit">Send Feedback</Trans>
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default FeedbackModal;
